import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { AssetForm } from '@/components/assets/asset-form';
import { useAsset, useUpdateAsset } from '@/hooks/use-assets';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import { handleApiError } from '@/lib/api-client';
import type { CreateAssetPayload } from '@/lib/types';

export function EditAssetPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const assetId = parseInt(id || '0', 10);

  const { data: asset, isLoading, isError } = useAsset(assetId);
  const updateMutation = useUpdateAsset();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (data: CreateAssetPayload) => {
    setSubmitError(null);
    try {
      await updateMutation.mutateAsync({ id: assetId, data });
      navigate(`/assets/${assetId}`);
    } catch (error) {
      const apiError = handleApiError(error);
      const fieldDetail = apiError.errors
        ? Object.entries(apiError.errors)
            .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
            .join(' ')
        : '';
      setSubmitError(fieldDetail || apiError.message || 'Failed to update asset.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  if (isError || !asset) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">Failed to load asset.</p>
          <Button asChild variant="outline">
            <Link to="/assets">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Assets
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Assets', href: '/assets' },
        { label: asset.display_name, href: `/assets/${assetId}` },
        { label: 'Edit' },
      ]} />
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to={`/assets/${assetId}`}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Asset</h1>
          <p className="text-gray-600 mt-1">
            Update information for {asset.display_name} ({asset.serial_number})
          </p>
        </div>
      </div>

      {submitError && (
        <Alert variant="destructive" className="max-w-2xl">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <div className="max-w-2xl">
        <AssetForm
          asset={asset}
          onSubmit={handleSubmit}
          isSubmitting={updateMutation.isPending}
          onCancel={() => navigate(`/assets/${assetId}`)}
        />
      </div>
    </div>
  );
}
